import { useMemo, useCallback } from "react";
import { useACLContext } from "../context";
import type { RoleName, Role } from "@shield-acl/core";

/**
 * Informações sobre a hierarquia de roles
 */
export interface RoleHierarchyInfo<TContext = any> {
  /** Todas as roles do usuário (incluindo herdadas) */
  allRoles: RoleName[];

  /** Roles diretas do usuário (não herdadas) */
  directRoles: RoleName[];

  /** Roles herdadas através de outras roles */
  inheritedRoles: RoleName[];

  /** Mapa de herança (role -> roles que ela herda) */
  inheritanceMap: Record<RoleName, RoleName[]>;

  /** Árvore de hierarquia completa */
  hierarchyTree: RoleHierarchyNode[];

  /** Verifica se usuário tem uma role específica (direta ou herdada) */
  hasRole: (roleName: RoleName) => boolean;

  /** Verifica se usuário tem alguma das roles listadas */
  hasAnyRole: (roleNames: RoleName[]) => boolean;

  /** Verifica se usuário tem todas as roles listadas */
  hasAllRoles: (roleNames: RoleName[]) => boolean;

  /** Obtém detalhes completos de uma role */
  getRoleDetails: (roleName: RoleName) => Role<TContext> | undefined;
}

/**
 * Nó da árvore de hierarquia
 */
export interface RoleHierarchyNode {
  name: RoleName;
  direct: boolean;
  children: RoleHierarchyNode[];
}

/**
 * Hook que fornece informações detalhadas sobre a hierarquia de roles do usuário
 *
 * @returns Objeto com informações e métodos sobre hierarquia de roles
 *
 * @example
 * ```tsx
 * const roleHierarchy = useRoleHierarchy()
 *
 * // Verificar roles
 * if (roleHierarchy.hasRole("admin")) {
 *   // É admin (direto ou herdado)
 * }
 *
 * if (roleHierarchy.hasAnyRole(["editor", "moderator"])) {
 *   // Tem pelo menos uma das roles
 * }
 *
 * // Visualizar hierarquia
 * console.log("Roles diretas:", roleHierarchy.directRoles)
 * console.log("Roles herdadas:", roleHierarchy.inheritedRoles)
 * console.log("Todas as roles:", roleHierarchy.allRoles)
 *
 * // Analisar herança
 * Object.entries(roleHierarchy.inheritanceMap).forEach(([role, inherits]) => {
 *   console.log(`${role} herda de: ${inherits.join(", ")}`)
 * })
 *
 * // Componente de visualização
 * function RoleTreeView() {
 *   const { hierarchyTree } = useRoleHierarchy()
 *
 *   return (
 *     <ul>
 *       {hierarchyTree.map(node => (
 *         <RoleNode key={node.name} node={node} />
 *       ))}
 *     </ul>
 *   )
 * }
 * ```
 */
export function useRoleHierarchy<
  TContext = any,
>(): RoleHierarchyInfo<TContext> {
  const { engine, user } = useACLContext<TContext>();

  // Calcula todas as roles (diretas + herdadas)
  const { allRoles, directRoles, inheritedRoles, inheritanceMap } =
    useMemo(() => {
      if (!user) {
        return {
          allRoles: [],
          directRoles: [],
          inheritedRoles: [],
          inheritanceMap: {},
        };
      }

      const direct = user.roles;
      const all = new Set<RoleName>(direct);
      const inheritance: Record<RoleName, RoleName[]> = {};

      // Função recursiva para coletar roles herdadas
      const collectInheritedRoles = (
        roleName: RoleName,
        visited = new Set<RoleName>(),
      ) => {
        if (visited.has(roleName)) return;
        visited.add(roleName);

        const role = engine.getRole(roleName);
        if (!role) return;

        inheritance[roleName] = role.inherits || [];

        if (role.inherits) {
          role.inherits.forEach((inheritedRole: RoleName) => {
            all.add(inheritedRole);
            collectInheritedRoles(inheritedRole, visited);
          });
        }
      };

      // Coleta todas as roles herdadas
      direct.forEach((roleName: RoleName) => collectInheritedRoles(roleName));

      const allArray = Array.from(all);
      const inherited = allArray.filter((role) => !direct.includes(role));

      return {
        allRoles: allArray,
        directRoles: direct,
        inheritedRoles: inherited,
        inheritanceMap: inheritance,
      };
    }, [engine, user]);

  // Constrói árvore de hierarquia
  const hierarchyTree = useMemo(() => {
    if (!user) return [];

    const buildNode = (
      roleName: RoleName,
      direct: boolean,
    ): RoleHierarchyNode => {
      const role = engine.getRole(roleName);
      const children =
        role?.inherits?.map((child: RoleName) => buildNode(child, false)) || [];

      return {
        name: roleName,
        direct,
        children,
      };
    };

    return directRoles.map((roleName: RoleName) => buildNode(roleName, true));
  }, [engine, user, directRoles]);

  // Função para verificar se tem uma role
  const hasRole = useCallback(
    (roleName: RoleName): boolean => {
      return allRoles.includes(roleName);
    },
    [allRoles],
  );

  // Função para verificar se tem alguma das roles
  const hasAnyRole = useCallback(
    (roleNames: RoleName[]): boolean => {
      return roleNames.some((role) => allRoles.includes(role));
    },
    [allRoles],
  );

  // Função para verificar se tem todas as roles
  const hasAllRoles = useCallback(
    (roleNames: RoleName[]): boolean => {
      return roleNames.every((role) => allRoles.includes(role));
    },
    [allRoles],
  );

  // Função para obter detalhes de uma role
  const getRoleDetails = useCallback(
    (roleName: RoleName): Role<TContext> | undefined => {
      return engine.getRole(roleName);
    },
    [engine],
  );

  return {
    allRoles,
    directRoles,
    inheritedRoles,
    inheritanceMap,
    hierarchyTree,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    getRoleDetails,
  };
}

/**
 * Hook simplificado que retorna apenas se o usuário tem determinadas roles
 *
 * @param roleNames - Role ou array de roles para verificar
 * @param mode - "any" (OR) ou "all" (AND), padrão é "any"
 * @returns boolean indicando se tem as roles
 *
 * @example
 * ```tsx
 * // Verificar uma role
 * const isAdmin = useHasRole("admin")
 *
 * // Verificar múltiplas roles (OR)
 * const canModerate = useHasRole(["moderator", "admin"])
 *
 * // Verificar múltiplas roles (AND)
 * const isSuperUser = useHasRole(["admin", "developer"], "all")
 * ```
 */
export function useHasRole(
  roleNames: RoleName | RoleName[],
  mode: "any" | "all" = "any",
): boolean {
  const { hasRole, hasAnyRole, hasAllRoles } = useRoleHierarchy();

  return useMemo(() => {
    const roles = Array.isArray(roleNames) ? roleNames : [roleNames];

    if (roles.length === 1) {
      return hasRole(roles[0]);
    }

    return mode === "all" ? hasAllRoles(roles) : hasAnyRole(roles);
  }, [roleNames, mode, hasRole, hasAnyRole, hasAllRoles]);
}

/**
 * Hook que retorna informações sobre uma role específica
 *
 * @param roleName - Nome da role
 * @returns Detalhes da role ou undefined
 *
 * @example
 * ```tsx
 * const adminRole = useRoleInfo("admin")
 *
 * if (adminRole) {
 *   console.log(`Role ${adminRole.name} tem ${adminRole.permissions.length} permissões`)
 *   console.log(`Herda de: ${adminRole.inherits?.join(", ") || "nenhuma"}`)
 * }
 * ```
 */
export function useRoleInfo<TContext = any>(
  roleName: RoleName,
): Role<TContext> | undefined {
  const { getRoleDetails } = useRoleHierarchy<TContext>();

  return useMemo(() => {
    return getRoleDetails(roleName);
  }, [getRoleDetails, roleName]);
}
